import React from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { fetchListeningStats } from '../../services/listeningStats';
import { fetchRecommendationProfiles } from '../../services/recommendationProfiles';
import { buildFavoritesByFeel } from '../../utils/favoritesFromListening';
import { parseYoutubeProviderAssetKey, youtubeMqThumbnailUrl } from '../../catalog/youtubeProviderAsset';

function favoriteToQueueTrack(item) {
  const parsed = parseYoutubeProviderAssetKey(item.providerAssetKey);
  if (!parsed?.videoId) return null;
  return {
    id: parsed.videoId,
    videoId: parsed.videoId,
    title: item.title || 'Track',
    artist: item.artist || '',
    thumbnail: item.artworkUrl || youtubeMqThumbnailUrl(parsed.videoId),
    source: 'favorites',
    recordingId: item.recordingId,
    providerAssetKey: item.providerAssetKey,
  };
}

export default function FavoritesSection({
  uid,
  onAddToQueue,
  canAdd = true,
}) {
  const [buckets, setBuckets] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!uid) {
      setBuckets([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const stats = await fetchListeningStats(uid, { max: 80 });
      if (cancelled) return;
      const profiles = await fetchRecommendationProfiles(
        stats.map((stat) => stat.recordingId),
      );
      if (cancelled) return;
      setBuckets(buildFavoritesByFeel({
        listeningStats: stats,
        profilesByRecordingId: profiles,
      }));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const hasAny = buckets.some((bucket) => bucket.items.length > 0);
  if (!uid) return null;
  if (!loading && !hasAny) return null;

  return (
    <section aria-label="Favorites" className="space-y-3" data-testid="favorites-section">
      <div>
        <h3 className="text-sm font-medium text-white">Favorites</h3>
        <p className="text-xs text-gray-400">From your listening history</p>
      </div>
      {loading ? (
        <p className="text-xs text-gray-500">Loading favorites…</p>
      ) : (
        <div className="space-y-4">
          {buckets.map((bucket) => {
            if (bucket.items.length === 0) return null;
            return (
              <div key={bucket.feelId}>
                <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                  {bucket.label}
                </h4>
                <ul className="space-y-1">
                  {bucket.items.map((item) => {
                    const track = favoriteToQueueTrack(item);
                    return (
                      <li
                        key={`${bucket.feelId}:${item.recordingId}`}
                        className="flex items-center gap-2 rounded-lg p-2 hover:bg-white/5"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm text-white">{item.title}</p>
                          {item.artist ? (
                            <p className="truncate text-xs text-gray-400">{item.artist}</p>
                          ) : null}
                        </div>
                        {canAdd && track ? (
                          <button
                            type="button"
                            aria-label={`Add ${item.title} to queue`}
                            onClick={() => onAddToQueue?.(track)}
                            className="rounded p-1 text-white/70 hover:bg-white/10 hover:text-white"
                          >
                            <PlusIcon className="h-5 w-5" />
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
